import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Space, Typography, theme, message } from 'antd';
import { 
  HomeOutlined, 
  ToolOutlined, 
  PlayCircleOutlined
} from '@ant-design/icons';
import FlowCanvas from './components/FlowBuilder/FlowCanvas';
import ExecutionSetupModal from './components/FlowBuilder/ExecutionSetupModal';
import AdRuleAPIService from './services/AdRuleAPIService';
import WorkflowAPIService from './services/WorkflowAPIService';
import globalVariableService from './services/GlobalVariableService';
import api from '../../services/api';
import { workflowPermissions } from '../../utils/permissions';
import {
    selectCurrentRule,
    selectLoading,
    selectIsExecuting,
    selectExecutionResult,
    selectShowExecutionModal,
    selectFlowData,
    setCurrentRule,
    setLoading,
    setExecuting,
    setExecutionResult,
    setShowExecutionModal
} from '../../redux/slices/adsOptimizationSlice';
import { NODE_DEFINITIONS } from './utils/nodeTypes';
import './AdsOptimization.css';

const { Title, Text } = Typography;

const FlowBuilder = () => {
    const { ruleId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Redux selectors
    const currentRule = useSelector(selectCurrentRule);
    const loading = useSelector(selectLoading);
    const executing = useSelector(selectIsExecuting);
    const executionResult = useSelector(selectExecutionResult);
    const showExecutionModal = useSelector(selectShowExecutionModal);
    const flowData = useSelector(selectFlowData);
    const { user } = useSelector(state => state.auth);
    const [showSetupModal, setShowSetupModal] = useState(false);
    
    // Permission checks
    const canUpdateWorkflow = workflowPermissions.canUpdate(user);
    const canExecuteWorkflow = workflowPermissions.canExecute(user);
    
    // Execution visualization state
    const [executingNodeId, setExecutingNodeId] = useState(null);
    const [completedNodeIds, setCompletedNodeIds] = useState([]);
    const [errorNodeId, setErrorNodeId] = useState(null);
    const [animatingEdgeIds, setAnimatingEdgeIds] = useState([]);
    
    // Debug mode state
    const [debugNodeId, setDebugNodeId] = useState(null);
    const [debugPausedAtNodeId, setDebugPausedAtNodeId] = useState(null);
    const [isDebugMode, setIsDebugMode] = useState(false);
    const [debugExecutionOrder, setDebugExecutionOrder] = useState([]);
    const [debugCurrentIndex, setDebugCurrentIndex] = useState(0);
    const [debugContext, setDebugContext] = useState({});
    const [debugNodeOverrides, setDebugNodeOverrides] = useState({}); // Store nodeOverrides for continue execution
    const [debugExecutionId, setDebugExecutionId] = useState(null); // Store executionId to reuse temp folder
    const [debugPreviousResults, setDebugPreviousResults] = useState({}); // Store node results for continuation
    const [pendingDebugNodeId, setPendingDebugNodeId] = useState(null); // Node to debug after setup modal
    
    // Suppress ResizeObserver loop errors
    useEffect(() => {
        const originalError = console.error;
        
        console.error = (...args) => {
            if (args[0] && (
                (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) || 
                (args[0] && args[0].message && args[0].message.includes('ResizeObserver loop'))
            )) {
                return;
            }
            originalError.call(console, ...args);
        };
        
        const handleError = (e) => {
            if (e.message && e.message.includes('ResizeObserver loop')) {
                e.stopImmediatePropagation();
                e.preventDefault(); // Prevent error overlay
            }
        };
        
        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleError);

        return () => {
            console.error = originalError;
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleError);
        };
    }, []);

    const containerStyle = {
        margin: '10px',
        padding: '16px',
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
        minHeight: 'calc(100vh - 80px)',
    };

    // Load rule and workflow data
    useEffect(() => {
        const loadRule = async () => {
            dispatch(setLoading(true));
            try {
                // Redirect to RuleList if trying to create new rule directly via URL
                // New rules should be created from RuleList → RuleForm first
                if (ruleId?.startsWith('new_') || ruleId?.startsWith('temp_')) {
                    message.warning('Vui lòng tạo rule từ trang quản lý Rules trước.');
                    navigate('/ads-tool');
                    return;
                }
                
                // Load existing rule from API
                const response = await AdRuleAPIService.getRule(ruleId);
                if (response.success) {
                    const rule = response.data;
                    
                    // Check if rule has workflow
                    if (!rule.workflowId) {
                        message.warning('Rule này chưa có workflow.');
                    }
                    
                    // Try to load workflow if workflowId exists
                    let workflowData = null;
                    if (rule.workflowId) {
                        try {
                            const workflowResponse = await WorkflowAPIService.getWorkflow(rule.workflowId);
                            if (workflowResponse.success) {
                                workflowData = workflowResponse.data;
                                
                                // Load variables into globalVariableService
                                if (workflowData.variables && typeof workflowData.variables === 'object') {
                                    globalVariableService.clearAll();
                                    // Variables are stored as {name: {value, timestamp, type}}
                                    Object.entries(workflowData.variables).forEach(([name, varData]) => {
                                        const value = typeof varData === 'object' && varData.value !== undefined 
                                            ? varData.value 
                                            : varData;
                                        globalVariableService.setVariable(name, value);
                                    });
                                    console.log('[FlowBuilder] Loaded variables:', Object.keys(workflowData.variables));
                                }
                            }
                        } catch (error) {
                            console.warn('Failed to load workflow:', error);
                        }
                    }
                    
                    const loadedRule = {
                        id: rule.id,
                        name: rule.name,
                        description: rule.description,
                        status: rule.isActive ? 'active' : 'inactive',
                        createdAt: new Date(rule.createdAt).toISOString().split('T')[0],
                        category: rule.type,
                        workflowId: rule.workflowId,
                        nodeCount: workflowData?.nodes?.length || rule.workflow?.nodes?.length || 0,
                        flow: {
                            nodes: workflowData?.nodes || rule.workflow?.nodes || [],
                            edges: workflowData?.edges || rule.workflow?.edges || []
                        },
                        workflow: workflowData || rule.workflow
                    };
                    dispatch(setCurrentRule(loadedRule));
                    message.success(`Loaded rule: ${rule.name}`);
                } else {
                    throw new Error(response.message || 'Rule not found');
                }
            } catch (error) {
                console.error('Error loading rule:', error);
                message.error('Failed to load rule: ' + error.message);
                navigate('/ads-tool');
            } finally {
                dispatch(setLoading(false));
            }
        };        if (ruleId) {
            loadRule();
        } else {
            navigate('/ads-tool');
        }
    }, [ruleId, navigate, dispatch]);

    const handleBackToRules = () => {
        navigate('/ads-tool');
    };

    // Execute workflow function
    const handleExecuteClick = () => {
        if (!flowData.nodes || flowData.nodes.length === 0) {
            message.warning('No nodes to execute. Please build a workflow first.');
            return;
        }
        // Open setup modal first
        setShowSetupModal(true);
    };

    // Helper to animate edge transition to a node
    const animateEdgeToNode = (nodeId, edges) => {
        const incomingEdges = edges.filter(e => e.target === nodeId);
        if (incomingEdges.length > 0) {
            setAnimatingEdgeIds(incomingEdges.map(e => e.id));
        }
    };

    // Helper to get SSE stream URL using the same base URL as api.js
    const getSSEStreamUrl = (executionId) => {
        // Get base URL from axios instance (same as api.js)
        const axiosBaseUrl = api.defaults.baseURL;
        // For SSE, we need the full URL including protocol and host
        // If baseURL is relative (like '/api'), construct full URL from window.location
        let fullBaseUrl;
        if (axiosBaseUrl.startsWith('http')) {
            fullBaseUrl = axiosBaseUrl;
        } else {
            // Relative URL - construct from current origin
            fullBaseUrl = `${window.location.origin}${axiosBaseUrl}`;
        }
        return `${fullBaseUrl}/workflow/stream/${executionId}`;
    };

    const executeWorkflow = async (context = {}) => {
        // Check if this is a debug execution
        if (pendingDebugNodeId) {
            return executeDebugToNode(pendingDebugNodeId, context);
        }
        
        setShowSetupModal(false);
        
        // If we don't have a workflow ID (new rule), we might need to save first
        // or use a special 'preview' endpoint. For now, let's require saving or use a placeholder if supported.
        const workflowId = currentRule.workflowId || currentRule.id;
        
        if (!workflowId || workflowId.toString().startsWith('new_')) {
             message.warning('Please save the rule before executing.');
             return;
        }

        // Reset execution visualization state
        setExecutingNodeId(null);
        setCompletedNodeIds([]);
        setErrorNodeId(null);
        setAnimatingEdgeIds([]);

        dispatch(setExecuting(true));
        dispatch(setExecutionResult(null));

        let eventSource = null;
        const completedIds = [];

        try {
            message.info('Starting workflow execution on backend...');
            
            // Start async execution to get executionId immediately
            const asyncResponse = await WorkflowAPIService.executeWorkflowAsync(workflowId, {
                nodes: flowData.nodes,
                edges: flowData.edges,
                variables: context.variables,
                nodeOverrides: context.nodeOverrides
            });
            
            if (!asyncResponse.success || !asyncResponse.data?.executionId) {
                throw new Error(asyncResponse.message || 'Failed to start workflow execution');
            }
            
            const { executionId } = asyncResponse.data;
            console.log('[FlowBuilder] Workflow started with executionId:', executionId);
            
            // Connect to SSE stream for real-time progress
            const streamUrl = getSSEStreamUrl(executionId);
            eventSource = new EventSource(streamUrl);
            
            // Create a promise that resolves when workflow completes
            const executionComplete = new Promise((resolve, reject) => {
                eventSource.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        console.log('[FlowBuilder] SSE Event:', data.type, data.payload);
                        
                        switch (data.type) {
                            case 'CONNECTED':
                                console.log('[FlowBuilder] SSE connected for execution:', data.executionId);
                                break;
                                
                            case 'WORKFLOW_STARTED':
                                message.info(`Executing ${data.payload?.nodeCount || 0} nodes...`);
                                break;
                                
                            case 'NODE_STARTED':
                                // Animate edge to this node, then set as executing
                                animateEdgeToNode(data.payload.nodeId, flowData.edges);
                                // Small delay to show edge animation before node starts
                                setTimeout(() => {
                                    setExecutingNodeId(data.payload.nodeId);
                                }, 200);
                                break;
                                
                            case 'NODE_COMPLETED':
                                // Mark node as completed
                                completedIds.push(data.payload.nodeId);
                                setCompletedNodeIds([...completedIds]);
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                break;
                                
                            case 'NODE_SKIPPED':
                                // Node was skipped (e.g., cached result in debug mode)
                                completedIds.push(data.payload.nodeId);
                                setCompletedNodeIds([...completedIds]);
                                break;
                                
                            case 'NODE_FAILED':
                                setErrorNodeId(data.payload.nodeId);
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                break;

                            // ===== BATCH PROCESSING EVENTS =====
                            case 'BATCH_STARTED':
                                console.log('[FlowBuilder] Batch started:', data.payload);
                                message.info(`Starting batch processing: ${data.payload?.iterationCount || 0} datasets`);
                                break;
                                
                            case 'BATCH_ITERATION_START':
                                console.log('[FlowBuilder] Batch iteration start:', data.payload);
                                // Show which dataset is being processed
                                message.info(`Processing: ${data.payload?.datasetName || `Dataset ${(data.payload?.iterationIndex || 0) + 1}`}`);
                                break;
                                
                            case 'BATCH_ITERATION_END':
                                console.log('[FlowBuilder] Batch iteration end:', data.payload);
                                break;
                                
                            case 'BATCH_COMPLETED':
                                console.log('[FlowBuilder] Batch completed:', data.payload);
                                message.success(`Batch processing completed: ${data.payload?.iterationCount || 0} datasets processed`);
                                break;
                            // ===== END BATCH PROCESSING EVENTS =====
                                
                            case 'WORKFLOW_COMPLETED':
                                // Mark all remaining nodes as completed
                                setExecutingNodeId(null);
                                setCompletedNodeIds(flowData.nodes.map(n => n.id));
                                setAnimatingEdgeIds([]);
                                resolve({ success: true, ...data.payload });
                                break;
                                
                            case 'WORKFLOW_FAILED':
                                console.log('[FlowBuilder] WORKFLOW_FAILED payload:', data.payload);
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                
                                // Try to find failed node from multiple sources
                                let failedNode = data.payload?.failedNodeId;
                                
                                // If no failedNodeId, try to find from completedIds
                                if (!failedNode && completedIds.length > 0) {
                                    // Find first node in execution order that's not completed
                                    const executionOrder = buildExecutionOrder(flowData.nodes, flowData.edges);
                                    failedNode = executionOrder.find(id => !completedIds.includes(id));
                                    console.log('[FlowBuilder] Found failed node from execution order:', failedNode);
                                }
                                
                                // If still no failedNode but we have nodes, use the first uncompleted one
                                if (!failedNode && flowData.nodes.length > 0) {
                                    const uncompleted = flowData.nodes.find(n => !completedIds.includes(n.id));
                                    failedNode = uncompleted?.id;
                                    console.log('[FlowBuilder] Fallback failed node:', failedNode);
                                }
                                
                                if (failedNode) {
                                    console.log('[FlowBuilder] Setting errorNodeId:', failedNode);
                                    setErrorNodeId(failedNode);
                                }
                                
                                // Include partial results so user can see what completed before error
                                resolve({ 
                                    success: false, 
                                    error: data.payload?.error,
                                    failedNodeId: failedNode,
                                    results: data.payload?.partialResults || {}
                                });
                                break;
                                
                            case 'LOG':
                                // Optional: Could show log messages in UI
                                console.log('[Workflow Log]', data.payload?.level, data.payload?.message);
                                break;
                                
                            default:
                                console.log('[FlowBuilder] Unknown SSE event type:', data.type);
                        }
                    } catch (parseError) {
                        console.error('[FlowBuilder] Error parsing SSE event:', parseError);
                    }
                };
                
                eventSource.onerror = (error) => {
                    console.error('[FlowBuilder] SSE error:', error);
                    // Don't reject immediately - the connection might recover
                    // Only reject if we haven't received completion
                    if (eventSource.readyState === EventSource.CLOSED) {
                        reject(new Error('SSE connection closed unexpectedly'));
                    }
                };
            });
            
            // Wait for workflow to complete via SSE
            const sseResult = await executionComplete;
            
            // Close SSE connection
            eventSource.close();
            eventSource = null;
            
            // Fetch final result with full data (SSE only sends preview)
            const finalResponse = await WorkflowAPIService.getExecutionResult(executionId);
            const result = finalResponse.success && finalResponse.data ? finalResponse.data : sseResult;
            
            dispatch(setExecutionResult(result));
            dispatch(setShowExecutionModal(true));
            
            if (result.success) {
                message.success(`Workflow executed successfully`);
            } else {
                // Set error node - try multiple sources
                let errorNode = result.failedNodeId;
                
                if (!errorNode) {
                    // Try to find from result
                    errorNode = findFailedNodeFromResult(result, flowData.nodes);
                }
                
                if (!errorNode && completedIds.length > 0) {
                    // Fallback: find first node not in completedIds
                    const executionOrder = buildExecutionOrder(flowData.nodes, flowData.edges);
                    errorNode = executionOrder.find(id => !completedIds.includes(id));
                }
                
                if (errorNode) {
                    console.log('[FlowBuilder] Setting error node:', errorNode);
                    setErrorNodeId(errorNode);
                } else {
                    console.warn('[FlowBuilder] Could not determine failed node');
                }
                
                message.error(`Workflow execution failed: ${result.error || result.message}`);
            }
        } catch (error) {
            console.error('Execution error:', error);
            // Find the node that was executing when error occurred
            // We need to check the current state or use a fallback
            const failedNode = flowData.nodes.find(n => 
                completedIds.length > 0 
                    ? !completedIds.includes(n.id) 
                    : true
            );
            setExecutingNodeId(null);
            if (failedNode) {
                setErrorNodeId(failedNode.id);
            }
            message.error('Failed to execute workflow: ' + error.message);
            
            // Close SSE if still open
            if (eventSource) {
                eventSource.close();
            }
        } finally {
            dispatch(setExecuting(false));
            // Clear animation after a delay
            setTimeout(() => {
                setAnimatingEdgeIds([]);
            }, 1000);
        }
    };

    // Build execution order based on graph topology
    // Excludes decoration nodes (comments, shapes, text annotations) from execution
    const buildExecutionOrder = (nodes, edges) => {
        // Filter out decoration nodes - they are purely visual
        const executableNodes = nodes.filter(node => {
            const nodeType = node.data?.nodeType || node.data?.type;
            const nodeDef = NODE_DEFINITIONS[nodeType];
            return !nodeDef?.isDecoration;
        });
        
        const order = [];
        const visited = new Set();
        const inDegree = {};
        
        // Initialize in-degree
        executableNodes.forEach(node => {
            inDegree[node.id] = 0;
        });
        
        // Calculate in-degree (only for executable nodes)
        edges.forEach(edge => {
            if (inDegree[edge.target] !== undefined) {
                inDegree[edge.target]++;
            }
        });
        
        // Find start nodes (in-degree = 0)
        const queue = executableNodes.filter(node => inDegree[node.id] === 0);
        
        while (queue.length > 0) {
            const node = queue.shift();
            if (!visited.has(node.id)) {
                visited.add(node.id);
                order.push(node.id);
                
                // Find connected nodes
                edges.forEach(edge => {
                    if (edge.source === node.id && !visited.has(edge.target)) {
                        if (inDegree[edge.target] !== undefined) {
                            inDegree[edge.target]--;
                            if (inDegree[edge.target] === 0) {
                                const targetNode = executableNodes.find(n => n.id === edge.target);
                                if (targetNode) queue.push(targetNode);
                            }
                        }
                    }
                });
            }
        }
        
        // Add any remaining executable nodes (in case of cycles or disconnected nodes)
        executableNodes.forEach(node => {
            if (!visited.has(node.id)) {
                order.push(node.id);
            }
        });
        
        return order;
    };

    // Simulate execution progress for visualization
    const simulateExecutionProgress = async (executionOrder, edges, stopAtNodeId = null) => {
        const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
        const completedIds = [];
        
        for (let i = 0; i < executionOrder.length; i++) {
            const nodeId = executionOrder[i];
            
            // Find edges going to this node and animate them
            const incomingEdges = edges.filter(e => e.target === nodeId);
            if (incomingEdges.length > 0) {
                setAnimatingEdgeIds(incomingEdges.map(e => e.id));
                await delay(500); // Time for edge animation
            }
            
            // Set current node as processing
            setExecutingNodeId(nodeId);
            
            // Wait to show processing state
            await delay(300);
            
            // Mark as completed
            completedIds.push(nodeId);
            setCompletedNodeIds([...completedIds]);
            setAnimatingEdgeIds([]);
            
            // Stop if we've reached the debug node
            if (stopAtNodeId && nodeId === stopAtNodeId) {
                setExecutingNodeId(null);
                return completedIds; // Return completed nodes for debug
            }
            
            // Small delay before next node
            await delay(100);
        }
        
        setExecutingNodeId(null);
        return completedIds;
    };

    // Debug to specific node - open setup modal first (same as execute)
    const handleDebugToNode = (targetNodeId) => {
        if (!flowData.nodes || flowData.nodes.length === 0) {
            message.warning('No nodes to debug. Please build a workflow first.');
            return;
        }
        
        // Check if debug target node exists in execution path
        const fullExecutionOrder = buildExecutionOrder(flowData.nodes, flowData.edges);
        if (!fullExecutionOrder.includes(targetNodeId)) {
            message.error('Target node not found in execution order');
            return;
        }
        
        // Store the target node and open setup modal
        setPendingDebugNodeId(targetNodeId);
        setShowSetupModal(true);
    };

    // Execute debug after setup modal - Using SSE pattern like normal execute
    const executeDebugToNode = async (targetNodeId, context = {}) => {
        setShowSetupModal(false);
        setPendingDebugNodeId(null);
        
        const workflowId = currentRule.workflowId || currentRule.id;
        
        if (!workflowId || workflowId.toString().startsWith('new_')) {
            message.warning('Please save the rule before debugging.');
            return;
        }

        // Reset execution visualization state
        setExecutingNodeId(null);
        setCompletedNodeIds([]);
        setErrorNodeId(null);
        setAnimatingEdgeIds([]);
        setIsDebugMode(true);
        setDebugPausedAtNodeId(null);

        dispatch(setExecuting(true));
        dispatch(setExecutionResult(null));

        let eventSource = null;
        const completedIds = [];

        try {
            message.info(`Starting debug execution to node...`);
            
            // Build execution order and find nodes up to target
            const fullExecutionOrder = buildExecutionOrder(flowData.nodes, flowData.edges);
            const targetIndex = fullExecutionOrder.indexOf(targetNodeId);
            
            if (targetIndex === -1) {
                message.error('Target node not found in execution order');
                dispatch(setExecuting(false));
                setIsDebugMode(false);
                return;
            }
            
            // Get nodes to execute (up to and including target)
            const nodesToExecute = fullExecutionOrder.slice(0, targetIndex + 1);
            const nodesToExecuteData = flowData.nodes.filter(n => nodesToExecute.includes(n.id));
            const edgesToExecute = flowData.edges.filter(e => 
                nodesToExecute.includes(e.source) && nodesToExecute.includes(e.target)
            );
            
            // Store debug state for step-by-step
            setDebugExecutionOrder(fullExecutionOrder);
            setDebugCurrentIndex(targetIndex);
            setDebugNodeOverrides(context.nodeOverrides || {}); // Save nodeOverrides for Next steps
            
            // Start ASYNC execution to get executionId immediately (SSE pattern)
            const asyncResponse = await WorkflowAPIService.executeWorkflowAsync(workflowId, {
                nodes: nodesToExecuteData,
                edges: edgesToExecute,
                variables: context.variables || {},
                nodeOverrides: context.nodeOverrides || {},
                debugMode: true,
                stopAtNodeId: targetNodeId
            });
            
            if (!asyncResponse.success || !asyncResponse.data?.executionId) {
                throw new Error(asyncResponse.message || 'Failed to start debug execution');
            }
            
            const { executionId } = asyncResponse.data;
            console.log('[FlowBuilder] Debug started with executionId:', executionId);
            
            // Save executionId for continuing debug
            setDebugExecutionId(executionId);
            
            // Connect to SSE stream for real-time progress
            const streamUrl = getSSEStreamUrl(executionId);
            eventSource = new EventSource(streamUrl);
            
            // Create a promise that resolves when workflow completes
            const executionComplete = new Promise((resolve, reject) => {
                eventSource.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        console.log('[FlowBuilder] Debug SSE Event:', data.type, data.payload);
                        
                        switch (data.type) {
                            case 'CONNECTED':
                                console.log('[FlowBuilder] Debug SSE connected for execution:', data.executionId);
                                break;
                                
                            case 'WORKFLOW_STARTED':
                                message.info(`Debugging ${data.payload?.nodeCount || 0} nodes...`);
                                break;
                                
                            case 'NODE_STARTED':
                                // Animate edge to this node, then set as executing
                                animateEdgeToNode(data.payload.nodeId, flowData.edges);
                                setTimeout(() => {
                                    setExecutingNodeId(data.payload.nodeId);
                                }, 200);
                                break;
                                
                            case 'NODE_COMPLETED':
                                // Mark node as completed
                                completedIds.push(data.payload.nodeId);
                                setCompletedNodeIds([...completedIds]);
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                break;
                                
                            case 'NODE_SKIPPED':
                                completedIds.push(data.payload.nodeId);
                                setCompletedNodeIds([...completedIds]);
                                break;
                                
                            case 'NODE_FAILED':
                                setErrorNodeId(data.payload.nodeId);
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                break;

                            // ===== BATCH PROCESSING EVENTS =====
                            case 'BATCH_STARTED':
                                console.log('[FlowBuilder Debug] Batch started:', data.payload);
                                message.info(`Starting batch processing: ${data.payload?.iterationCount || 0} datasets`);
                                break;
                                
                            case 'BATCH_ITERATION_START':
                                console.log('[FlowBuilder Debug] Batch iteration start:', data.payload);
                                message.info(`Processing: ${data.payload?.datasetName || `Dataset ${(data.payload?.iterationIndex || 0) + 1}`}`);
                                break;
                                
                            case 'BATCH_ITERATION_END':
                                console.log('[FlowBuilder Debug] Batch iteration end:', data.payload);
                                break;
                                
                            case 'BATCH_COMPLETED':
                                console.log('[FlowBuilder Debug] Batch completed:', data.payload);
                                message.success(`Batch processing completed: ${data.payload?.iterationCount || 0} datasets processed`);
                                break;
                            // ===== END BATCH PROCESSING EVENTS =====
                                
                            case 'WORKFLOW_COMPLETED':
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                resolve({ success: true, ...data.payload, executionId });
                                break;
                                
                            case 'WORKFLOW_FAILED':
                                console.log('[FlowBuilder executeDebugToNode] WORKFLOW_FAILED:', data.payload);
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                
                                // Find failed node
                                let dbgFailedNode = data.payload?.failedNodeId;
                                if (!dbgFailedNode && completedIds.length > 0) {
                                    const execOrder = buildExecutionOrder(flowData.nodes, flowData.edges);
                                    dbgFailedNode = execOrder.find(id => !completedIds.includes(id));
                                }
                                if (!dbgFailedNode && flowData.nodes.length > 0) {
                                    dbgFailedNode = flowData.nodes.find(n => !completedIds.includes(n.id))?.id;
                                }
                                if (dbgFailedNode) {
                                    console.log('[FlowBuilder executeDebugToNode] Setting errorNodeId:', dbgFailedNode);
                                    setErrorNodeId(dbgFailedNode);
                                }
                                
                                resolve({ 
                                    success: false, 
                                    error: data.payload?.error, 
                                    failedNodeId: dbgFailedNode,
                                    executionId 
                                });
                                break;
                                
                            case 'LOG':
                                console.log('[Debug Workflow Log]', data.payload?.level, data.payload?.message);
                                break;
                                
                            default:
                                console.log('[FlowBuilder] Unknown Debug SSE event type:', data.type);
                        }
                    } catch (parseError) {
                        console.error('[FlowBuilder] Error parsing Debug SSE event:', parseError);
                    }
                };
                
                eventSource.onerror = (error) => {
                    console.error('[FlowBuilder] Debug SSE error:', error);
                    if (eventSource.readyState === EventSource.CLOSED) {
                        reject(new Error('Debug SSE connection closed unexpectedly'));
                    }
                };
            });
            
            // Wait for workflow to complete via SSE
            const sseResult = await executionComplete;
            
            // Close SSE connection
            eventSource.close();
            eventSource = null;
            
            // Fetch final result with full data
            const finalResponse = await WorkflowAPIService.getExecutionResult(executionId);
            const result = finalResponse.success && finalResponse.data ? finalResponse.data : sseResult;
            
            // Save node results for next step (skip already executed nodes)
            if (result.results) {
                setDebugPreviousResults(result.results);
            }
            
            // Mark debug result
            result.isDebugResult = true;
            result.debugStoppedAt = targetNodeId;
            result.debugNodeName = flowData.nodes.find(n => n.id === targetNodeId)?.data?.label || 
                                   flowData.nodes.find(n => n.id === targetNodeId)?.data?.nodeType;
            result.executionId = executionId;
            
            // Set paused state at target node
            setDebugPausedAtNodeId(targetNodeId);
            setDebugContext({ ...context, ...result }); // Store context for next step
            
            dispatch(setExecutionResult(result));
            dispatch(setShowExecutionModal(true));
            
            if (result.success) {
                message.success(`Debug paused at "${result.debugNodeName}" - Click "Next" to continue`);
            } else {
                const failedNodeId = findFailedNodeFromResult(result, flowData.nodes);
                if (failedNodeId) {
                    setErrorNodeId(failedNodeId);
                }
                setIsDebugMode(false);
                message.error(`Debug execution failed: ${result.error || result.message}`);
            }
        } catch (error) {
            console.error('Debug execution error:', error);
            setExecutingNodeId(null);
            setErrorNodeId(targetNodeId);
            setIsDebugMode(false);
            message.error('Failed to debug workflow: ' + error.message);
            
            // Close SSE if still open
            if (eventSource) {
                eventSource.close();
            }
        } finally {
            dispatch(setExecuting(false));
            setTimeout(() => {
                setAnimatingEdgeIds([]);
            }, 1000);
        }
    };

    // Debug next node - continue step by step - Using SSE pattern
    const handleDebugNextNode = async () => {
        if (!isDebugMode || !debugPausedAtNodeId) return;
        
        const currentIndex = debugExecutionOrder.indexOf(debugPausedAtNodeId);
        const nextIndex = currentIndex + 1;
        
        if (nextIndex >= debugExecutionOrder.length) {
            message.info('Reached end of workflow');
            handleStopDebug();
            return;
        }
        
        const nextNodeId = debugExecutionOrder[nextIndex];
        const workflowId = currentRule.workflowId || currentRule.id;
        
        dispatch(setExecuting(true));
        
        let eventSource = null;
        const completedIds = [...completedNodeIds]; // Keep previously completed nodes
        
        try {
            // Get nodes to execute (from start to next node)
            const nodesToExecute = debugExecutionOrder.slice(0, nextIndex + 1);
            const nodesToExecuteData = flowData.nodes.filter(n => nodesToExecute.includes(n.id));
            const edgesToExecute = flowData.edges.filter(e => 
                nodesToExecute.includes(e.source) && nodesToExecute.includes(e.target)
            );
            
            // Animate edge to next node
            const incomingEdges = flowData.edges.filter(e => e.target === nextNodeId);
            if (incomingEdges.length > 0) {
                setAnimatingEdgeIds(incomingEdges.map(e => e.id));
            }
            
            // Set next node as processing
            setExecutingNodeId(nextNodeId);
            
            // Start ASYNC execution to get executionId immediately (SSE pattern)
            const asyncResponse = await WorkflowAPIService.executeWorkflowAsync(workflowId, {
                nodes: nodesToExecuteData,
                edges: edgesToExecute,
                variables: debugContext.variables || {},
                nodeOverrides: debugNodeOverrides, // Use saved nodeOverrides
                executionId: debugExecutionId, // Reuse same executionId to keep temp folder
                previousResults: debugPreviousResults, // Skip already executed nodes
                debugMode: true,
                stopAtNodeId: nextNodeId
            });
            
            if (!asyncResponse.success || !asyncResponse.data?.executionId) {
                throw new Error(asyncResponse.message || 'Failed to start next debug step');
            }
            
            const { executionId } = asyncResponse.data;
            console.log('[FlowBuilder] Debug next step started with executionId:', executionId);
            
            // Update executionId in case backend generates a new one
            if (executionId !== debugExecutionId) {
                setDebugExecutionId(executionId);
            }
            
            // Connect to SSE stream for real-time progress
            const streamUrl = getSSEStreamUrl(executionId);
            eventSource = new EventSource(streamUrl);
            
            // Create a promise that resolves when workflow completes
            const executionComplete = new Promise((resolve, reject) => {
                eventSource.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        console.log('[FlowBuilder] Debug Next SSE Event:', data.type, data.payload);
                        
                        switch (data.type) {
                            case 'CONNECTED':
                                console.log('[FlowBuilder] Debug Next SSE connected:', data.executionId);
                                break;
                                
                            case 'WORKFLOW_STARTED':
                                // Don't override message since we already showed one
                                break;
                                
                            case 'NODE_STARTED':
                                setExecutingNodeId(data.payload.nodeId);
                                break;
                                
                            case 'NODE_COMPLETED':
                                if (!completedIds.includes(data.payload.nodeId)) {
                                    completedIds.push(data.payload.nodeId);
                                    setCompletedNodeIds([...completedIds]);
                                }
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                break;
                                
                            case 'NODE_SKIPPED':
                                if (!completedIds.includes(data.payload.nodeId)) {
                                    completedIds.push(data.payload.nodeId);
                                    setCompletedNodeIds([...completedIds]);
                                }
                                break;
                                
                            case 'NODE_FAILED':
                                setErrorNodeId(data.payload.nodeId);
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                break;
                                
                            case 'WORKFLOW_COMPLETED':
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                resolve({ success: true, ...data.payload, executionId });
                                break;
                                
                            case 'WORKFLOW_FAILED':
                                console.log('[FlowBuilder handleDebugNextNode] WORKFLOW_FAILED:', data.payload);
                                setExecutingNodeId(null);
                                setAnimatingEdgeIds([]);
                                
                                // Find failed node - use nextNodeId as fallback since that's what we're executing
                                let nextFailedNode = data.payload?.failedNodeId || nextNodeId;
                                if (nextFailedNode) {
                                    console.log('[FlowBuilder handleDebugNextNode] Setting errorNodeId:', nextFailedNode);
                                    setErrorNodeId(nextFailedNode);
                                }
                                
                                resolve({ 
                                    success: false, 
                                    error: data.payload?.error, 
                                    failedNodeId: nextFailedNode,
                                    executionId 
                                });
                                break;
                                
                            case 'LOG':
                                console.log('[Debug Next Log]', data.payload?.level, data.payload?.message);
                                break;
                                
                            default:
                                console.log('[FlowBuilder] Unknown Debug Next SSE event:', data.type);
                        }
                    } catch (parseError) {
                        console.error('[FlowBuilder] Error parsing Debug Next SSE:', parseError);
                    }
                };
                
                eventSource.onerror = (error) => {
                    console.error('[FlowBuilder] Debug Next SSE error:', error);
                    if (eventSource.readyState === EventSource.CLOSED) {
                        reject(new Error('Debug Next SSE connection closed unexpectedly'));
                    }
                };
            });
            
            // Wait for workflow to complete via SSE
            const sseResult = await executionComplete;
            
            // Close SSE connection
            eventSource.close();
            eventSource = null;
            
            // Fetch final result with full data
            const finalResponse = await WorkflowAPIService.getExecutionResult(executionId);
            const result = finalResponse.success && finalResponse.data ? finalResponse.data : sseResult;
            
            // Update completed nodes
            setCompletedNodeIds(nodesToExecute);
            setExecutingNodeId(null);
            setAnimatingEdgeIds([]);
            
            // Update previousResults for next step
            if (result.results) {
                setDebugPreviousResults(result.results);
            }
            
            // Mark debug result
            result.isDebugResult = true;
            result.debugStoppedAt = nextNodeId;
            result.debugNodeName = flowData.nodes.find(n => n.id === nextNodeId)?.data?.label || 
                                   flowData.nodes.find(n => n.id === nextNodeId)?.data?.nodeType;
            result.executionId = executionId;
            
            // Update paused state
            setDebugPausedAtNodeId(nextNodeId);
            setDebugCurrentIndex(nextIndex);
            setDebugContext(result);
            
            dispatch(setExecutionResult(result));
            
            if (result.success) {
                if (nextIndex === debugExecutionOrder.length - 1) {
                    message.success(`Debug completed! Reached end of workflow.`);
                    handleStopDebug();
                } else {
                    message.success(`Paused at "${result.debugNodeName}" - Click "Next" to continue`);
                }
            } else {
                const failedNodeId = findFailedNodeFromResult(result, flowData.nodes);
                if (failedNodeId) {
                    setErrorNodeId(failedNodeId);
                }
                setIsDebugMode(false);
                message.error(`Debug execution failed: ${result.error || result.message}`);
            }
        } catch (error) {
            console.error('Debug next error:', error);
            setExecutingNodeId(null);
            setErrorNodeId(nextNodeId);
            message.error('Failed to execute next step: ' + error.message);
            
            // Close SSE if still open
            if (eventSource) {
                eventSource.close();
            }
        } finally {
            dispatch(setExecuting(false));
            setTimeout(() => {
                setAnimatingEdgeIds([]);
            }, 500);
        }
    };

    // Stop debugging
    const handleStopDebug = () => {
        setIsDebugMode(false);
        setDebugPausedAtNodeId(null);
        setDebugNodeId(null);
        setDebugExecutionOrder([]);
        setDebugCurrentIndex(0);
        setDebugContext({});
        setDebugNodeOverrides({}); // Clear saved nodeOverrides
        setDebugExecutionId(null); // Clear executionId
        setDebugPreviousResults({}); // Clear previous results
        message.info('Debug session ended');
    };

    // Find failed node from execution result - improved detection
    const findFailedNodeFromResult = (result, nodes) => {
        // 1. Check from executionLog
        if (result.executionLog) {
            const failedLog = result.executionLog.find(log => log.status === 'error' || log.error);
            if (failedLog && failedLog.nodeId) {
                return failedLog.nodeId;
            }
        }
        
        // 2. Check from results object - find node with error status
        if (result.results) {
            for (const [nodeId, nodeResult] of Object.entries(result.results)) {
                if (nodeResult && (nodeResult.error || nodeResult.status === 'error' || nodeResult.success === false)) {
                    return nodeId;
                }
            }
        }
        
        // 3. Check error message for node reference
        if (result.error && typeof result.error === 'string') {
            // Try to extract nodeId from error message if present
            const nodeMatch = result.error.match(/node[_\s]?([a-zA-Z0-9_-]+)/i);
            if (nodeMatch) {
                const potentialNodeId = nodeMatch[0];
                if (nodes.find(n => n.id === potentialNodeId)) {
                    return potentialNodeId;
                }
            }
        }
        
        // 4. Fallback: find first node that's not in completed results
        if (result.results && nodes.length > 0) {
            const completedNodeIds = Object.keys(result.results).filter(
                nodeId => result.results[nodeId]?.success !== false
            );
            const incompleteNode = nodes.find(n => !completedNodeIds.includes(n.id));
            if (incompleteNode) {
                return incompleteNode.id;
            }
        }
        
        return null;
    };

    const handleEditRule = () => {
        // Navigate back to main page with edit mode
        navigate('/ads-tool', { state: { editRuleId: ruleId } });
    };

    return (
        <div style={{
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            overflow: 'hidden',
            height: '100%',
            position: 'relative',
            background: colorBgContainer
        }}>
            {loading ? (
                <div style={{
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <Text>Loading rule...</Text>
                </div>
            ) : !currentRule ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text>Rule not found</Text>
                    <div style={{ marginTop: 16 }}>
                        <Button onClick={handleBackToRules}>Back to Rules</Button>
                    </div>
                </div>
            ) : (
                <FlowCanvas
                    currentRule={currentRule}
                    ruleInfo={currentRule ? {
                        name: currentRule.name,
                        description: currentRule.description,
                        category: currentRule.category,
                        status: currentRule.status,
                        nodeCount: currentRule.nodeCount || 0
                    } : null}
                    onBackToRules={handleBackToRules}
                    onEditRule={handleEditRule}
                    onExecuteWorkflow={canExecuteWorkflow ? handleExecuteClick : null}
                    onDebugToNode={canExecuteWorkflow ? handleDebugToNode : null}
                    onDebugNextNode={canExecuteWorkflow ? handleDebugNextNode : null}
                    onStopDebug={handleStopDebug}
                    isExecuting={executing}
                    executionResult={executionResult}
                    showExecutionPanel={showExecutionModal}
                    onShowExecutionResults={() => dispatch(setShowExecutionModal(true))}
                    onCloseExecutionResults={() => dispatch(setShowExecutionModal(false))}
                    executingNodeId={executingNodeId}
                    completedNodeIds={completedNodeIds}
                    errorNodeId={errorNodeId}
                    animatingEdgeIds={animatingEdgeIds}
                    debugNodeId={debugNodeId}
                    debugPausedAtNodeId={debugPausedAtNodeId}
                    isDebugMode={isDebugMode}
                    onSetDebugNode={(nodeId) => setDebugNodeId(nodeId)}
                    onClearDebugNode={() => setDebugNodeId(null)}
                    canUpdateWorkflow={canUpdateWorkflow}
                    canExecuteWorkflow={canExecuteWorkflow}
                />
            )}

            {/* Execution Setup Modal */}
            <ExecutionSetupModal
                open={showSetupModal}
                onCancel={() => {
                    setShowSetupModal(false);
                    setPendingDebugNodeId(null); // Clear pending debug when cancelled
                }}
                onExecute={executeWorkflow}
                nodes={flowData.nodes}
                edges={flowData.edges}
                isDebugMode={!!pendingDebugNodeId}
                debugNodeName={pendingDebugNodeId ? 
                    (flowData.nodes.find(n => n.id === pendingDebugNodeId)?.data?.label || 
                     flowData.nodes.find(n => n.id === pendingDebugNodeId)?.data?.nodeType || 
                     'node') : null
                }
            />
        </div>
    );
};

export default FlowBuilder;